import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  varchar,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const notificationSeverityEnum = z.enum(["info", "warning", "urgent"]);
export type NotificationSeverity = z.infer<typeof notificationSeverityEnum>;

export const notificationStatusEnum = z.enum(["unread", "read", "archived"]);
export type NotificationStatus = z.infer<typeof notificationStatusEnum>;

export const notificationCategoryEnum = z.enum([
  "enrollment_activity",
  "enrollment_risk",
  "coverage_changes",
  "qle",
  "terminations",
  "cobra",
  "eligibility_milestones",
  "renewals",
  "integrations",
  "discrepancies",
  "open_enrollment",
  "payroll",
  "system",
  "compliance",
  "prospects",
  "commissions",
  "client_health",
  "automation",
  // User-facing communication switches (Settings → Notifications)
  "email_all",
  "marketing",
  "tasks",
]);
export type NotificationCategory = z.infer<typeof notificationCategoryEnum>;

export const portalTypeEnum = z.enum(["broker", "employer", "employee"]);
export type PortalType = z.infer<typeof portalTypeEnum>;

export const notifications = pgTable(
  "notifications",
  {
    id: serial("id").primaryKey(),
    tenantId: integer("tenant_id"),
    portalType: text("portal_type").notNull().$type<PortalType>(),
    orgId: integer("org_id"),
    clientId: integer("client_id"),
    relatedEntityType: text("related_entity_type"),
    relatedEntityId: integer("related_entity_id"),
    severity: text("severity").notNull().$type<NotificationSeverity>().default("info"),
    category: text("category").notNull().$type<NotificationCategory>(),
    title: text("title").notNull(),
    message: text("message").notNull(),
    ctaLabel: text("cta_label"),
    ctaUrl: text("cta_url"),
    audienceRoles: text("audience_roles").array(),
    recipientUserId: varchar("recipient_user_id"),
    status: text("status").notNull().$type<NotificationStatus>().default("unread"),
    deliveredChannels: text("delivered_channels").array().default([]),
    dedupeKey: text("dedupe_key"),
    expiresAt: timestamp("expires_at"),
    templateData: jsonb("template_data").$type<Record<string, string>>(),
    createdAt: timestamp("created_at").defaultNow(),
    readAt: timestamp("read_at"),
  },
  (table) => [
    index("notifications_recipient_idx").on(table.recipientUserId),
    index("notifications_portal_org_idx").on(table.portalType, table.orgId),
    index("notifications_status_idx").on(table.status),
    index("notifications_dedupe_idx").on(table.dedupeKey),
  ]
);

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  createdAt: true,
  readAt: true,
});

export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type Notification = typeof notifications.$inferSelect;

export const notificationPreferences = pgTable(
  "notification_preferences",
  {
    id: serial("id").primaryKey(),
    tenantId: integer("tenant_id"),
    portalType: text("portal_type").notNull().$type<PortalType>(),
    orgId: integer("org_id"),
    userId: varchar("user_id"),
    category: text("category").notNull().$type<NotificationCategory>(),
    enabled: boolean("enabled").notNull().default(true),
    emailEnabled: boolean("email_enabled").notNull().default(false),
    digestMode: boolean("digest_mode").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("notification_preferences_user_portal_category_unique").on(
      table.userId,
      table.portalType,
      table.category,
    ),
  ],
);

export const insertNotificationPreferenceSchema = createInsertSchema(notificationPreferences).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertNotificationPreference = z.infer<typeof insertNotificationPreferenceSchema>;
export type NotificationPreference = typeof notificationPreferences.$inferSelect;

export const notificationTemplates = pgTable("notification_templates", {
  id: serial("id").primaryKey(),
  code: text("code").notNull().unique(),
  portalType: text("portal_type").notNull().$type<PortalType>(),
  category: text("category").notNull().$type<NotificationCategory>(),
  severity: text("severity").notNull().$type<NotificationSeverity>(),
  titleTemplate: text("title_template").notNull(),
  messageTemplate: text("message_template").notNull(),
  ctaLabel: text("cta_label"),
  ctaUrlTemplate: text("cta_url_template"),
  audienceRoles: text("audience_roles").array(),
  dedupeWindowHours: integer("dedupe_window_hours").default(24),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertNotificationTemplateSchema = createInsertSchema(notificationTemplates).omit({
  id: true,
  createdAt: true,
});

export type InsertNotificationTemplate = z.infer<typeof insertNotificationTemplateSchema>;
export type NotificationTemplate = typeof notificationTemplates.$inferSelect;
